import { useState, useCallback, useMemo } from 'react';

/**
 * SOLD 스타일 관련 상태 및 로직을 관리하는 커스텀 훅
 * - deactivation 스타일 (dim, line, text)
 * - 라인 설정 (두께, 길이, 색상)
 * - 텍스트 설정 (내용, 색상, 굵기, 크기, 회전, 기울기)
 * 
 * Note: 스타일은 planche_settings (DB)에서 복원됨
 *       localStorage는 사용하지 않음 (통일된 스타일 보장)
 */
const useSoldStyle = () => {
    // Deactivation 스타일 - 기본값 'dim', DB에서 복원됨
    const [deactivationStyle, setDeactivationStyle] = useState('dim');

    // 라인 설정
    const [lineWidth, setLineWidth] = useState(3);
    const [lineLength, setLineLength] = useState(100);
    const [lineColor, setLineColor] = useState('#ff0000');

    // 텍스트 설정
    const [soldText, setSoldText] = useState('TAKEN');
    const [soldTextColor, setSoldTextColor] = useState('#ff0000');
    const [soldTextWeight, setSoldTextWeight] = useState(700);
    const [soldTextSize, setSoldTextSize] = useState(20);
    const [soldTextRotation, setSoldTextRotation] = useState(-15);
    const [soldTextItalic, setSoldTextItalic] = useState(false);

    // 스타일 변경 (DB에 저장될 때 planche_settings로 저장됨)
    const updateDeactivationStyle = useCallback((style) => {
        setDeactivationStyle(style);
    }, []);

    // 라인 스타일 일괄 업데이트
    const updateLineStyle = useCallback((updates) => {
        if (updates.width !== undefined) setLineWidth(updates.width);
        if (updates.length !== undefined) setLineLength(updates.length);
        if (updates.color !== undefined) setLineColor(updates.color);
    }, []);

    // 텍스트 스타일 일괄 업데이트
    const updateTextStyle = useCallback((updates) => {
        if (updates.text !== undefined) setSoldText(updates.text);
        if (updates.color !== undefined) setSoldTextColor(updates.color);
        if (updates.weight !== undefined) setSoldTextWeight(updates.weight);
        if (updates.size !== undefined) setSoldTextSize(updates.size);
        if (updates.rotation !== undefined) setSoldTextRotation(updates.rotation);
        if (updates.italic !== undefined) setSoldTextItalic(updates.italic);
    }, []);

    // 현재 스타일 객체 (메모이제이션)
    const lineStyle = useMemo(() => ({
        width: lineWidth,
        length: lineLength,
        color: lineColor,
    }), [lineWidth, lineLength, lineColor]);

    const textStyle = useMemo(() => ({
        text: soldText,
        color: soldTextColor,
        weight: soldTextWeight,
        size: soldTextSize,
        rotation: soldTextRotation,
        italic: soldTextItalic,
    }), [soldText, soldTextColor, soldTextWeight, soldTextSize, soldTextRotation, soldTextItalic]);

    return {
        // 상태
        deactivationStyle,
        lineWidth,
        lineLength,
        lineColor,
        soldText,
        soldTextColor,
        soldTextWeight,
        soldTextSize,
        soldTextRotation,
        soldTextItalic,

        // 개별 setter (하위 호환성)
        setDeactivationStyle: updateDeactivationStyle,
        setLineWidth,
        setLineLength,
        setLineColor,
        setSoldText,
        setSoldTextColor,
        setSoldTextWeight,
        setSoldTextSize,
        setSoldTextRotation,
        setSoldTextItalic,

        // 일괄 업데이트
        updateLineStyle,
        updateTextStyle,

        // 스타일 객체
        lineStyle,
        textStyle,
    };
};

export default useSoldStyle;
